import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { LanguageSelector } from '../components/LanguageSelector';
import { Loader2, Mail, Lock, ArrowLeft, MessageCircle, Phone, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { isInAppBrowser, getInAppBrowserName } from '../utils/browserDetect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [deviceBlocked, setDeviceBlocked] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/contact`).then(res => setContactSettings(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDeviceBlocked(false);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid credentials';
      if (detail === 'DEVICE_LIMIT_REACHED') {
        setDeviceBlocked(true);
      } else if (error.response?.status === 403) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const isWebView = isInAppBrowser();

  const handleGoogleLogin = () => {
    if (isWebView) {
      toast.error(`Google Sign-In is blocked in ${getInAppBrowserName()}. Please open in Chrome or Safari.`);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to dashboard (main app) - Emergent Auth will append #session_id to this URL
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="login-page">
      {/* Background pattern */}
      <div className="absolute inset-0 hero-pattern opacity-30" />
      
      {/* Back button */}
      <Link 
        to="/" 
        className="absolute top-4 left-4 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>{t('back')}</span>
      </Link>

      {/* Language selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="ghost" />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700/50 bg-card/95 backdrop-blur">
        <CardHeader className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg" 
            alt="EGNS Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <CardTitle className="font-heading text-2xl">{t('login')}</CardTitle>
          <CardDescription>Welcome back to EGNS German</CardDescription>
        </CardHeader>

        <CardContent>
          {deviceBlocked ? (
            <div className="space-y-4" data-testid="device-blocked">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <p className="text-red-400 font-semibold mb-2">Device Limit Reached</p>
                <p className="text-sm text-muted-foreground mb-1">
                  This activation code is already linked to another device.
                </p>
                <p className="text-sm text-muted-foreground">
                  Each code can only be used on one device.
                </p>
              </div>
              <div className="bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/30 rounded-lg p-4">
                <p className="text-sm font-medium text-teal-400 mb-3 text-center">Contact us to purchase a new code:</p>
                <div className="flex gap-2">
                  <a 
                    href={contactSettings ? `https://wa.me/${contactSettings.whatsapp_number}?text=${encodeURIComponent(contactSettings.whatsapp_message || '')}` : '#'}
                    target="_blank" rel="noopener noreferrer" className="flex-1"
                    data-testid="device-blocked-whatsapp"
                  >
                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                      <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                    </Button>
                  </a>
                  <a href={contactSettings ? `tel:${contactSettings.phone_number}` : '#'} className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Phone className="h-4 w-4 mr-2" /> Call Us
                    </Button>
                  </a>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">{contactSettings?.phone_display || ''}</p>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setDeviceBlocked(false)}>
                Try Again
              </Button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('password')}</Label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  {t('forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-teal-500 hover:bg-teal-600" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('login')
              )}
            </Button>
          </form>
          )}

          {!deviceBlocked && (
          <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t('orContinueWith')}</span>
            </div>
          </div>

          {isWebView && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center" data-testid="webview-warning">
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
                You're using {getInAppBrowserName()}'s browser
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Google Sign-In doesn't work here. Use email/password or open in your browser:
              </p>
              <Button size="sm" variant="outline" className="text-xs border-amber-500/40 text-amber-600" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied! Paste it in Chrome or Safari.'); }}>
                <ExternalLink className="h-3 w-3 mr-1" /> Copy link to open in browser
              </Button>
            </div>
          )}

          <Button 
            variant="outline" 
            className={`w-full ${isWebView ? 'opacity-50' : ''}`}
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {t('google')}
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-2">
            For existing accounts only. New users: <Link to="/register" className="text-primary hover:underline">Register here</Link>
          </p>
          </>
          )}
        </CardContent>

        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            {t('noAccount')}{' '}
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
              {t('register')}
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
